import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import './Toolkit.css';

const FILE_ICONS = {
  pdf: '📄',
  doc: '📝',
  docx: '📝',
  xls: '📊',
  xlsx: '📊',
  png: '🖼️',
  jpg: '🖼️',
  jpeg: '🖼️',
  gif: '🖼️',
  mp4: '🎥',
  mov: '🎥',
  mp3: '🎵',
  txt: '📃',
};

function getFileIcon(fileType) {
  return FILE_ICONS[fileType?.toLowerCase()] || '📎';
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Toolkit() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) loadDocs();
  }, [user]);

  async function loadDocs() {
    setLoading(true);

    // RLS handles filtering — returns global docs from practitioner + per-client docs
    const { data } = await supabase
      .from('toolkit_documents')
      .select('*')
      .order('sort_order')
      .order('created_at', { ascending: false });

    setDocs(data || []);
    setLoading(false);
  }

  if (loading) {
    return (
      <div className="screen container">
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--color-text-muted)' }}>
          Loading toolkit...
        </div>
      </div>
    );
  }

  const personalDocs = docs.filter((d) => d.client_id);
  const globalDocs = docs.filter((d) => !d.client_id);

  return (
    <div className="screen container toolkit-screen">
      <div className="toolkit-header">
        <h1>Toolkit</h1>
        <p className="toolkit-subtitle">Reference documents from your practitioner</p>
      </div>

      {docs.length === 0 ? (
        <div className="toolkit-empty">
          <p>📂</p>
          <p>No documents available yet.</p>
          <p className="toolkit-empty-hint">Your practitioner will upload resources here for you.</p>
        </div>
      ) : (
        <>
          {/* Personal docs */}
          {personalDocs.length > 0 && (
            <div className="toolkit-section">
              <h3 className="toolkit-section-title">For You</h3>
              <div className="toolkit-doc-list">
                {personalDocs.map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="toolkit-doc-card"
                  >
                    <span className="toolkit-doc-icon">{getFileIcon(doc.file_type)}</span>
                    <div className="toolkit-doc-info">
                      <span className="toolkit-doc-name">{doc.name}</span>
                      {doc.description && <p className="toolkit-doc-desc">{doc.description}</p>}
                      <p className="toolkit-doc-meta">
                        {doc.file_type?.toUpperCase()}{doc.file_size ? ` · ${formatFileSize(doc.file_size)}` : ''}
                      </p>
                    </div>
                    <span className="toolkit-doc-arrow">›</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Global docs */}
          {globalDocs.length > 0 && (
            <div className="toolkit-section">
              <h3 className="toolkit-section-title">Resources</h3>
              <div className="toolkit-doc-list">
                {globalDocs.map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="toolkit-doc-card"
                  >
                    <span className="toolkit-doc-icon">{getFileIcon(doc.file_type)}</span>
                    <div className="toolkit-doc-info">
                      <span className="toolkit-doc-name">{doc.name}</span>
                      {doc.description && <p className="toolkit-doc-desc">{doc.description}</p>}
                      <p className="toolkit-doc-meta">
                        {doc.file_type?.toUpperCase()}{doc.file_size ? ` · ${formatFileSize(doc.file_size)}` : ''}
                      </p>
                    </div>
                    <span className="toolkit-doc-arrow">›</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
